import * as actionTypes from "./actionTypes";
import * as userService from "./service";
import * as listService from "../Lists/service";
import constants from "../../utils/constants";
import { signedUserData } from "../../utils/user";
import { clearAllColumnFilters } from "../../hooks/useColumnFilters";

const log = require("loglevel").getLogger("UserActions");
log.setLevel("error");

export const signIn = (idToken, onSuccess, onFail) => {
  return async (dispatch) => {
    log.debug("UserActions -> signIn -> Enter");

    const response = await userService.signIn(idToken);

    log.debug("UserActions -> signIn -> User signed in. response = ", response);

    if (response) {
      clearAllColumnFilters();

      await dispatch(setLoggedInUser(response));
      
      // Fetch default lists and store in localStorage
      try {
        const defaultLists = await listService.getDefaultLists();
        
        // Store default lists in localStorage
        if (defaultLists) {
          const allLists = {};
          defaultLists.forEach(list => {
            if (!allLists[list.entity_type]) {
              allLists[list.entity_type] = [];
            }
            allLists[list.entity_type].push(list);
            localStorage.setItem(`defaultList-${list.entity_type}`, list.id);
          });
          // localStorage.setItem('userLists', JSON.stringify(allLists));
        }
      } catch (error) {
        log.error("Error fetching default lists during login:", error);
        // Continue with login even if list fetching fails
      }
      
      onSuccess?.();
    } else {
      onFail?.();
    }
  };
};

export const checkInvite = (hash, callback) => {
  return async (dispatch) => {
    log.debug("UserActions -> checkInvite -> Enter");
    const user = await userService.checkInvite(hash);
    callback?.(user);
  };
};

export const registerUser = (email, uid, callback) => {
  return async (dispatch) => {
    log.debug("UserActions -> registerUser -> Enter");
    const user = await userService.registerUser(email, uid);
    callback?.(user);
  };
};

export const loadContacts = (accountName, callback) => {
  return async (dispatch) => {
    log.debug("UserActions -> loadContacts -> Enter");
    const users = (await userService.loadContacts(accountName)) ?? [];
    callback?.(users);
  };
};

export const createExternalUser = (user, callback) => {
  return async (dispatch) => {
    log.debug("UserActions -> createExternalUser -> Enter");
    const result = await userService.createExternalUser(user);
    callback?.(result);
  };
};

export const createInternalUser = (email, name, account, callback) => {
  return async (dispatch) => {
    log.debug("UserActions -> createInternalUser -> Enter");
    const result = await userService.createInternalUser(email, name, account);
    callback?.(result);
  };
};

export const saveUserMatch = (inputName, matchedName, vendor) => {
  return async (dispatch) => {
    log.debug("UserActions -> saveUserMatch -> Enter");
    await userService.saveUserMatch(inputName, matchedName, vendor);
  };
};

export const unsaveUserMatch = (inputName, matchedName, vendor) => {
  return async (dispatch) => {
    log.debug("UserActions -> saveUserMatch -> Enter");
    await userService.unsaveUserMatch(inputName, matchedName, vendor);
  };
};

let latestRequestIdMap = {
  aws: 0,
  ms: 0,
};

export const getVendorAccounts = (vendor, limit, offset, selectedFilters, callback) => {
  return async (dispatch) => {
    const requestId = ++latestRequestIdMap[vendor];

    const result = await userService.getVendorAccounts(vendor, limit, offset, selectedFilters) ?? {};

    if (requestId !== latestRequestIdMap[vendor]) return;

    callback?.(result);

    dispatch({
      type: actionTypes.ACCOUNTS_LOADED,
      data: result.accounts ?? [],
      total: result.total ?? 0,
      vendor,
    });
  };
};

export const toggleUserEnabled = (email) => {
  return async (dispatch) => {
    await userService.toggleUserEnabled(email);

    await dispatch({
      type: actionTypes.TOGGLE_USER_ENABLED,
      data: email,
    });
  };
};

export const runSearch = (searchTerm, searchOption, callback) => {
  return async (dispatch) => {
    log.debug("UserActions -> runSearch -> Enter");
    const searchResults =
      (await userService.searchResults(searchTerm, searchOption)) ?? [];
    callback?.(searchResults);
  };
};

export const changeUserDatabase = (email, database, callback) => {
  return async (dispatch) => {
    log.debug("UserActions -> changeUserDatabase -> Enter");
    const result =
      (await userService.changeUserDatabase(email, database)) ?? {};

    if (result?.adminNewDatabase != null) {
      const user = JSON.parse(signedUserData() ?? "{}");

      if (result.adminNewDatabase === "sandbox") {
        user.onsandbox = true;
      } else {
        delete user.onsandbox;
      }
      await dispatch(setLoggedInUser(user));
    }

    if (result?.status === "ok") {
      await dispatch({
        type: actionTypes.USERS_LOADED,
        data: result?.users ?? [],
      });
    } else {
      callback?.(result);
    }
  };
};

export const loadUsers = () => {
  return async (dispatch) => {
    log.debug("UserActions -> loadUsers -> Enter");
    const users = (await userService.loadUsers()) ?? [];

    await dispatch({
      type: actionTypes.USERS_LOADED,
      data: users,
    });
  };
};

export const switchAccount = (account, callback) => {
  return async (dispatch) => {
    log.debug("UserActions -> switchAccount -> Enter");
    const userData = await userService.switchAccount(account);

    log.debug("UserActions -> switchAccount -> new userData = ", userData);

    await dispatch(setLoggedInUser(userData));

    callback?.();
  };
};

export const updateSavedListAccounts = (listId, accounts) => {
  return async (dispatch) => {
    log.debug("UserActions -> updateSavedListAccounts -> Enter");
    await userService.updateSavedListAccounts(listId, accounts);
  };
};

export const ping = () => {
  return async (dispatch) => {
    log.debug("UserActions -> ping -> Enter");
    await userService.ping();
  };
};

export const runReport = (vendor, query, callback) => {
  return async (dispatch) => {
    log.debug("UserActions -> runReport -> Enter");
    const reportData =
      (await userService.runReport(vendor, query)) ?? {};
    callback?.(reportData);
  };
};

export const extractOpportunities = (callback) => {
  return async (dispatch) => {
    log.debug("UserActions -> extractOpportunities -> Enter");
    const data = (await userService.extractOpportunities()) ?? {};
    callback?.(data);
  };
};

export const loadMoreSimilarities = (
  vendor,
  value,
  pageNumber,
  amount,
  callback
) => {
  return async (dispatch) => {
    log.debug("UserActions -> loadMoreSimilarities -> Enter");
    const result =
      (await userService.loadMoreSimilarities(
        vendor,
        value,
        pageNumber,
        amount
      )) ?? {};
    callback?.(result);
  };
};

export const deleteUserList = (id, callback) => {
  return async (dispatch) => {
    log.debug("UserActions -> deleteUserList -> Enter");
    const result = await userService.deleteUserList(id);
    callback?.(result);
  };
};

export const loadUserList = (row, callback) => {
  return async (dispatch) => {
    log.debug("UserActions -> loadUserList -> Enter");
    const result = await userService.loadUserList(row);
    callback?.(result);
  };
};

export const getAllUserLists = (callback) => {
  return async (dispatch) => {
    log.debug("UserActions -> getAllUserLists -> Enter");
    const result = (await userService.getAllUserLists()) ?? [];
    callback?.(result);
  };
};

export const saveList = (
  name,
  description,
  accountNames,
  excludedAccounts,
  fileName,
  vendor,
  callback
) => {
  return async (dispatch) => {
    log.debug("UserActions -> saveList -> Enter");
    const result = await userService.saveList(
      name,
      description,
      accountNames,
      excludedAccounts,
      fileName,
      vendor
    );
    callback?.(result);
  };
};

export const loadSuggestions = (vendor, regions, segments, value, callback) => {
  return async (dispatch) => {
    log.debug("UserActions -> loadSuggestions -> Enter");
    const result =
      (await userService.loadSuggestions(vendor, regions, segments, value)) ??
      [];
    callback?.(result);
  };
};

export const searchAccounts = (
  vendor,
  accountNames,
  regions,
  segments,
  callback
) => {
  return async (dispatch) => {
    log.debug("UserActions -> searchAccounts -> Enter");
    const result = await userService.searchAccounts(
      vendor,
      accountNames,
      regions,
      segments
    );
      callback?.(result);
  };
};

export const setLoggedInUser = (userData) => {
  log.debug("UserActions -> setLoggedInUser -> Enter");
  return async (dispatch) => {
    await dispatch({
      type: actionTypes.USER_SIGNED_IN,
      data: userData,
    });
  };
};

export const updateUserToken = (idToken) => {
  log.debug("UserActions -> updateUserToken -> Enter");
  return async (dispatch) => {
    await dispatch({
      type: actionTypes.UPDATE_USER_TOKEN,
      data: idToken,
    });
  };
};

export const updateSavedListExcludedRows = (listId, excludedAccounts) => {
  log.debug("UserActions -> updateSavedListExcludedRows -> Enter");
  return async (dispatch) => {
    await userService.updateSavedListExcludedRows(listId, excludedAccounts);
  };
};

export const signOut = (callback) => {
  return async (dispatch) => {
    const logoutResult = await userService.signOut();
    log.debug("signOut result", logoutResult);

    await dispatch({
      type: actionTypes.USER_SIGNED_OUT,
    });
    callback?.();
  };
};

export const requestDownload = (numOfRecords, callback) => {
  return async (dispatch) => {
    const result = await userService.requestDownload(numOfRecords);
    callback?.(result);
  };
};

export const toggleMenu = () => {
  log.debug("UserActions -> toggleMenu");
  return {
    type: actionTypes.TOGGLE_MENU,
  };
};

export const resetState = () => {
  log.debug("UserActions -> performing resetState");
  return {
    type: constants.GLOBAL_ACTION_INIT_STATE,
  };
};
